import type { SideBySideListBuilderItem } from '@amzn/storm-ui';
import {
  EligibleAccount,
  CombineAccountType,
} from 'src/shared/services/combine-accounts/types';
import {
  CHOOSE_ACCOUNTS_FILTER_ALL_KEY,
  CHOOSE_ACCOUNTS_FILTER_DSP_KEY,
  CHOOSE_ACCOUNTS_FILTER_SA_KEY,
} from '@features/combine-accounts/constants';
import { AdProductGroup } from 'src/shared/models/account';

export const DEFAULT_PAGE_SIZE = 100;

// Manager Account children are filtered server-side, so a smaller page is cheaper and the
// BFF auto-follows empty pages. Top-level browse uses DEFAULT_PAGE_SIZE (100).
export const MA_CHILDREN_PAGE_SIZE = 50;

// Values are Puff string keys, resolved at render time by the filter dropdown.
// The keys ('all', 'sa', 'dsp') are the filter logic's vocabulary and are
// unaffected by translation.
export const FILTER_OPTIONS = {
  all: CHOOSE_ACCOUNTS_FILTER_ALL_KEY,
  sa: CHOOSE_ACCOUNTS_FILTER_SA_KEY,
  dsp: CHOOSE_ACCOUNTS_FILTER_DSP_KEY,
} as const;
export type FilterKey = keyof typeof FILTER_OPTIONS;

// Named so callers pin a lane's default filter without a raw string literal.
export const FILTER_KEY_ALL: FilterKey = 'all';
export const FILTER_KEY_SA: FilterKey = 'sa';
export const FILTER_KEY_DSP: FilterKey = 'dsp';

// Translates a "Filter by" dropdown key into the ad product group the BFF
// filters on. 'sa' -> SPONSORED_ADS, 'dsp' -> ADSP. 'all' is intentionally
// undefined, meaning "send no filter": the exact-set "All" semantics (whether
// "All" must also exclude SGAs) are still open, so for now callers treat
// undefined as "omit the filter param".
export const FILTER_KEY_TO_AD_PRODUCT_GROUP: Record<
  FilterKey,
  AdProductGroup | undefined
> = {
  all: undefined,
  sa: AdProductGroup.SPONSORED_ADS,
  dsp: AdProductGroup.ADSP,
};

export type WizardStep = 'choose-accounts' | 'account-details' | 'review';

export interface AccountItem extends SideBySideListBuilderItem {
  accountType: CombineAccountType;
  isPendingAccess?: boolean;
  eligibilityStatus?: { isEligible: boolean; reasonCode?: string };
  parentManagerAccountId?: string;
}

export const isDSPAccount = (id: string): boolean => /^\d+[a-zA-Z]?$/.test(id);
export const isSAAccount = (id: string): boolean =>
  id.startsWith('amzn1.ads-account');

export const getAccountTypeFromId = (id: string): CombineAccountType | null => {
  if (isDSPAccount(id)) return CombineAccountType.DSP;
  if (isSAAccount(id)) return CombineAccountType.SA;
  return null;
};

export const toItem = (a: EligibleAccount): AccountItem => ({
  id: a.accountId,
  label: a.accountName,
  description: a.accountId,
  accountType: a.accountType,
  eligibilityStatus: a.eligibilityStatus,
});

export const toChildItem = (
  a: EligibleAccount,
  parentId: string,
): AccountItem => ({
  ...toItem(a),
  parentManagerAccountId: parentId,
});
